<template>
  <div class="min-h-screen bg-gray-50 flex flex-col font-sans selection:bg-orange-100 selection:text-orange-900">
    <Head title="Realgestión Prime — Potencia tu Negocio" />
    
    <!-- ══ HERO SECTION ══ -->
    <div class="relative overflow-hidden bg-white pt-16 pb-24 md:pt-24 md:pb-32">
      <!-- Background Decor -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div class="absolute -top-24 -right-24 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-50" />
        <div class="absolute top-1/2 -left-24 w-72 h-72 bg-purple-100 rounded-full blur-3xl opacity-30" />
      </div>

      <div class="relative container mx-auto px-4 text-center">
        <Logo class="mx-auto mb-8 h-10 w-auto" />
        <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-100 text-orange-600 text-xs font-bold uppercase tracking-wider mb-8 animate-fade-in">
          <i class="fas fa-crown" /> Subir de Nivel es Ahora
        </div>
        
        <h1 class="text-4xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 mb-8 tracking-tight leading-[1.1]">
          Impulsa tu Negocio al <br />
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-orange-500 to-rose-500">Nivel Prime</span>
        </h1>
        
        <p class="text-lg md:text-xl text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed">
          Has intentado acceder a una función exclusiva. Desbloquea herramientas de gestión avanzada, automatización y reportes que harán crecer tu empresa.
        </p>

        <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/subscription/plans" class="w-full sm:w-auto px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-2xl shadow-xl shadow-orange-200 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2">
            Ver Planes y Precios
            <i class="fas fa-arrow-right text-xs" />
          </Link>
          <Link href="/dashboard" class="w-full sm:w-auto px-8 py-4 bg-white border border-gray-200 text-gray-600 font-bold rounded-2xl hover:bg-gray-50 transition-all flex items-center justify-center gap-2">
            Volver al Dashboard
          </Link>
        </div>
      </div>
    </div>

    <!-- ══ FEATURES BENTO GRID ══ -->
    <div class="py-24 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-black text-gray-900 mb-4 tracking-tight">Todo lo que necesitas para escalar</h2>
          <p class="text-gray-500 max-w-xl mx-auto">Funcionalidades diseñadas por expertos en gestión empresarial para que tú solo te preocupes por vender.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6 max-w-7xl mx-auto">
          <!-- FEATURE: equipo -->
          <div class="md:col-span-3 lg:col-span-4 group p-8 rounded-3xl bg-blue-50/50 border border-blue-100 hover:bg-blue-50 transition-all duration-500 flex flex-col">
            <div class="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-blue-200 group-hover:scale-110 group-hover:rotate-3 transition-transform">
              <i class="fas fa-users text-xl" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">Equipo de Trabajo</h3>
            <p class="text-gray-600 text-sm leading-relaxed mb-6">
              Invita a tus vendedores, administradores y personal de almacén. Controla quién accede a qué con permisos detallados.
            </p>
            <div class="mt-auto flex -space-x-2">
              <div v-for="i in 4" :key="i" class="w-8 h-8 rounded-full border-2 border-white bg-blue-200" />
              <div class="w-8 h-8 rounded-full border-2 border-white bg-blue-500 flex items-center justify-center text-[10px] text-white font-bold">+10</div>
            </div>
          </div>

          <!-- FEATURE: Compras y Proveedores -->
          <div class="md:col-span-3 lg:col-span-8 group p-8 rounded-3xl bg-orange-50/50 border border-orange-100 hover:bg-orange-50 transition-all duration-500">
            <div class="flex flex-col md:flex-row md:items-center gap-8">
              <div class="flex-1">
                <div class="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-orange-200 group-hover:scale-110 group-hover:-rotate-3 transition-transform">
                  <i class="fas fa-shopping-cart text-xl" />
                </div>
                <h3 class="text-xl font-bold text-gray-900 mb-3">Control de Abastecimiento</h3>
                <p class="text-gray-600 text-sm leading-relaxed">
                  Gestiona tus órdenes de compra, controla las recepciones de mercancía y mantén un directorio completo de tus proveedores. Todo el ciclo de stock bajo control.
                </p>
              </div>
              <div class="flex-1 bg-white/60 rounded-2xl p-4 border border-orange-100/50 shadow-inner">
                <div class="space-y-3">
                  <div class="h-8 bg-orange-100/50 rounded-lg flex items-center px-3 justify-between">
                    <span class="text-[10px] font-bold text-orange-600 uppercase">Orden #1204</span>
                    <span class="px-2 py-0.5 rounded-full bg-green-500 text-white text-[8px] font-bold">RECIBIDA</span>
                  </div>
                  <div class="h-8 bg-gray-100/50 rounded-lg" />
                  <div class="h-8 bg-gray-100/50 rounded-lg" />
                </div>
              </div>
            </div>
          </div>

          <!-- FEATURE: Reportes Avanzados -->
          <div class="md:col-span-6 lg:col-span-7 group p-8 rounded-3xl bg-indigo-50/50 border border-indigo-100 hover:bg-indigo-50 transition-all duration-500 overflow-hidden relative">
            <div class="relative z-10">
              <div class="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-indigo-200 group-hover:scale-110 transition-transform">
                <i class="fas fa-chart-line text-xl" />
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Analytics & Inteligencia</h3>
              <p class="text-gray-600 text-sm leading-relaxed max-w-md">
                Toma decisiones basadas en datos. Obtén reportes detallados de ventas, rentabilidad, movimientos de caja y proyecciones de inventario. Exporta todo en PDF o Excel.
              </p>
            </div>
            <!-- Abstract Chart Decor -->
            <div class="absolute -bottom-4 -right-4 w-64 h-40 opacity-20 group-hover:opacity-40 transition-opacity">
              <div class="flex items-end gap-1 h-full">
                <div v-for="h in [20, 40, 30, 60, 45, 80, 50]" :key="h" :style="{ height: h + '%' }" class="flex-1 bg-indigo-500 rounded-t-lg" />
              </div>
            </div>
          </div>

          <!-- FEATURE: Gastos y Rentabilidad -->
          <div class="md:col-span-3 lg:col-span-5 group p-8 rounded-3xl bg-rose-50/50 border border-rose-100 hover:bg-rose-50 transition-all duration-500">
            <div class="w-12 h-12 rounded-2xl bg-rose-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-rose-200 group-hover:scale-110 group-hover:rotate-6 transition-transform">
              <i class="fas fa-wallet text-xl" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">Control Financiero</h3>
            <p class="text-gray-600 text-sm leading-relaxed">
              Registra cada gasto, pago a proveedores y egresos operativos. Visualiza la rentabilidad neta de tu negocio restando los costos automáticamente.
            </p>
          </div>

          <!-- FEATURE: Catálogo Pro -->
          <div class="md:col-span-3 lg:col-span-12 group p-8 rounded-3xl bg-amber-50/50 border border-amber-100 hover:bg-amber-50 transition-all duration-500">
            <div class="flex flex-col lg:flex-row gap-12 items-center">
              <div class="flex-1">
                <div class="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-amber-200 group-hover:scale-110 transition-transform">
                  <i class="fas fa-store text-xl" />
                </div>
                <h3 class="text-xl font-bold text-gray-900 mb-3">Catálogo Online Sin Límites</h3>
                <p class="text-gray-600 text-sm leading-relaxed mb-6">
                  Personaliza tu tienda con tus colores, logo y banners destacados. Sube productos ilimitados y recibe pedidos por WhatsApp sin comisión por ventas.
                </p>
                <div class="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wider">
                  <span class="px-2 py-1 bg-white border border-amber-200 rounded-lg text-amber-600">Logo Propio</span>
                  <span class="px-2 py-1 bg-white border border-amber-200 rounded-lg text-amber-600">Colores de Marca</span>
                  <span class="px-2 py-1 bg-white border border-amber-200 rounded-lg text-amber-600">Pedidos WhatsApp</span>
                  <span class="px-2 py-1 bg-white border border-amber-200 rounded-lg text-amber-600">SEO Optimizado</span>
                </div>
              </div>
              <div class="flex-1 w-full max-w-lg">
                <div class="relative bg-white rounded-3xl shadow-2xl p-4 overflow-hidden border border-gray-100 group-hover:translate-y-[-10px] transition-transform duration-700">
                  <div class="aspect-video bg-gray-50 rounded-2xl flex items-center justify-center overflow-hidden mb-4">
                    <i class="fas fa-image text-gray-200 text-5xl" />
                  </div>
                  <div class="space-y-2">
                    <div class="h-3 w-2/3 bg-gray-100 rounded-lg" />
                    <div class="h-3 w-1/3 bg-orange-100 rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ PLAN COMPARISON ══ -->
    <div class="py-24 bg-gray-50">
      <div class="container mx-auto px-4 max-w-4xl">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-black text-gray-900 tracking-tight">¿Por qué pasarse a Prime?</h2>
        </div>
        
        <div class="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50/50">
                <th class="p-6 text-sm font-bold text-gray-400 uppercase tracking-widest">Funcionalidad</th>
                <th class="p-6 text-sm font-bold text-gray-900 text-center uppercase tracking-widest bg-gray-100/30">Free</th>
                <th class="p-6 text-sm font-bold text-orange-600 text-center uppercase tracking-widest bg-orange-50/30">Prime</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50">
              <tr v-for="feature in comparisons" :key="feature.name" class="hover:bg-gray-50/50 transition-colors">
                <td class="p-6 text-sm font-bold text-gray-700">{{ feature.name }}</td>
                <td class="p-6 text-center text-gray-400">
                  <i v-if="feature.free" class="fas fa-check text-green-500" />
                  <span v-else>—</span>
                </td>
                <td class="p-6 text-center bg-orange-50/10">
                  <i v-if="feature.prime" class="fas fa-check-circle text-orange-500 scale-125" />
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="mt-12 text-center">
          <Link href="/subscription/plans" class="inline-flex items-center gap-2 group">
            <span class="text-lg font-bold text-gray-900 border-b-2 border-orange-200 group-hover:border-orange-500 transition-all pb-1">Ver todos los planes detallados</span>
            <i class="fas fa-chevron-right text-orange-500 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>

    <!-- ══ FINAL CTA ══ -->
    <div class="relative py-24 bg-white overflow-hidden">
      <!-- Full-width background banner -->
      <div class="w-full bg-gradient-to-br from-gray-900 via-gray-900 to-black text-white relative py-20 md:py-32 shadow-2xl">
        <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/20 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2 animate-pulse" />
        <div class="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple-600/10 blur-[100px] rounded-full -translate-x-1/2 translate-y-1/2" />
        
        <div class="container mx-auto px-4 text-center relative z-10">
          <h2 class="text-4xl md:text-6xl font-black mb-8 tracking-tighter leading-tight animate-fade-in">¿Listo para llevar tu <br class="hidden md:block" /> negocio al éxito?</h2>
          <p class="text-gray-400 mb-12 text-lg md:text-xl max-w-2xl mx-auto font-medium">Únete a cientos de empresas que ya están automatizando sus procesos con Realgestión.</p>
          
          <div class="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link href="/subscription/plans" class="w-full sm:w-auto px-12 py-5 bg-orange-500 hover:bg-orange-600 text-white font-black text-lg rounded-2xl shadow-2xl shadow-orange-500/40 transition-all hover:scale-110 active:scale-95 uppercase tracking-widest">
              EMPEZAR AHORA
            </Link>
            <Link href="/dashboard" class="w-full sm:w-auto px-8 py-5 border border-white/10 hover:bg-white/5 text-gray-400 hover:text-white font-bold rounded-2xl transition-all">
              Tal vez más tarde
            </Link>
          </div>
        </div>
      </div>
    </div>

    <footer class="py-12 border-t border-gray-100 bg-white">
      <div class="container mx-auto px-4 text-center">
        <p class="text-sm text-gray-400">Realgestión © {{ new Date().getFullYear() }}. Todos los derechos reservados.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Head, Link } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'

export default defineComponent({
  components: {
    Head,
    Link,
    Logo,
  },
  props: {
    account: Object,
  },
  data() {
    return {
      comparisons: [
        { name: 'Clientes y Productos Ilimitados', free: true, prime: true },
        { name: 'Ventas POS e Inventario Ilimitado', free: true, prime: true },
        { name: 'Compras y Gastos Ilimitados', free: true, prime: true },
        { name: 'Catálogo Digital para Clientes', free: true, prime: true },
        { name: 'Módulo de Cobranzas PRO (Portal + Avisos WhatsApp/Email)', free: false, prime: true },
        { name: 'Reportes de Ventas por Ganancia y Utilidad Real', free: false, prime: true },
        { name: 'Alertas Diarias de Stock Mínimo por Agotarse', free: false, prime: true },
        { name: 'Subir Compras Escaneando Facturas con IA', free: false, prime: true },
        { name: 'Cargar Transportistas Propios en Despachos', free: false, prime: true },
        { name: 'Comandas en Tiempo Real para Restaurantes (Cocina / Bar)', free: false, prime: true },
        { name: 'Soporte Prioritario 24/7', free: false, prime: true },
      ],
    }
  },
})
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.8s ease-out forwards;
}
</style>
